import { SortingState } from "@tanstack/react-table";
import { PlusInSquareIcon } from "assets/icons";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useDebouncedValue } from "hooks";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { SortingOrder } from "libs/api";
import { useRefetchQuery } from "libs/reactQuery";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Pagination, SearchInput } from "ui";

import { useDeletePack, useGetPacks, useTogglePackActive } from "../../apis";
import { PacksTable } from "../../components";
import { PACKS_QUERY_KEY } from "../../constants";
import { PackListItem } from "../../packs.types";

import styles from "./Packs.module.scss";

const Packs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const debouncedSearch = useDebouncedValue(searchValue, 300);

  const sortParam = sorting[0];

  const requestParams = useMemo(
    () => ({
      take: GET_LIST_REQUEST_DEFAULT_LIMIT,
      skip: (page - 1) * GET_LIST_REQUEST_DEFAULT_LIMIT,
      search: debouncedSearch || undefined,
      orderBy: sortParam?.id,
      order: sortParam
        ? ((sortParam.desc ? "DESC" : "ASC") as SortingOrder)
        : undefined
    }),
    [page, debouncedSearch, sortParam]
  );

  const { packs, meta, isLoading } = useGetPacks(requestParams);
  const refetch = useRefetchQuery([PACKS_QUERY_KEY]);

  const toggleMutation = useTogglePackActive({
    onSuccess: () => {
      void refetch();
    }
  });

  const deleteMutation = useDeletePack({
    onSuccess: () => {
      toast.success(t("Pack deleted"));
      void refetch();
    }
  });

  const handleDelete = (pack: PackListItem) => {
    if (window.confirm(t("Delete this pack?"))) {
      deleteMutation.mutate(pack.id);
    }
  };

  const totalItems = meta?.itemCount ?? 0;

  return (
    <ContentLayout
      title={
        <span className={styles.packs__title}>
          {t("Packs")}
          <span className={styles.packs__count}>({totalItems})</span>
        </span>
      }
      action={
        <Button
          onClick={() => navigate("/packs/new")}
          icon={<PlusInSquareIcon />}
        >
          {t("New Pack")}
        </Button>
      }
    >
      <BoxShadowLayout className={styles.packs__content}>
        <div className={styles.packs__header}>
          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            placeholder={t("Search packs...")}
          />
        </div>

        <PacksTable
          packs={packs ?? []}
          isLoading={isLoading}
          sorting={sorting}
          setSorting={setSorting}
          onRowClick={(pack) => navigate(`/packs/${pack.id}`)}
          onEdit={(pack) => navigate(`/packs/${pack.id}/edit`)}
          onDelete={handleDelete}
          onToggleActive={(pack) =>
            toggleMutation.mutate({
              packId: pack.id,
              isActive: !pack.isActive
            })
          }
        />

        <Pagination
          currentPage={page}
          onChange={setPage}
          perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
          totalItems={totalItems}
        />
      </BoxShadowLayout>
    </ContentLayout>
  );
};

export default Packs;
